import { Component, computed, effect, input, InputSignal, model, output, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { WsAgGridWrapperComponent } from '../../../../Compliance/AgGrid/AgGridWrapper/wsAgGridWrapper.component';
import Guid = System.Guid;
import { AgGridEvent, ApplyColumnStateParams, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridOptionsBuilder } from '../../../../Compliance/AgGrid';
import { ReferralInputWithRevertComponent } from '../Input-With-Revert/referralInputWithRevert.component';
import { MessageModalService } from 'src/app/UI-Lib/Message-Box/messageModal.service';
import { BusyIndicatorService } from 'src/app/Busy-Indicator';
import { ReferralsService } from '../../referrals.service';
import { WeissmanModalService } from 'src/app/Compliance/WeissmanModalService';
import { AddAppealsModalComponent } from './addAppealsModal.component';
import { ReferralPageService } from '../referralPage.service';
import { REFERRAL_APPEAL_COLUMNS } from './referralAppealColumns';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { WeissmanDateFormatPipe } from '../../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';

@Component({
    selector: 'referral-site-details',
    templateUrl: 'siteDetails.component.html',
    styleUrls: ['../referralPage.component.scss'],
    imports: [ReactiveFormsModule, WsAgGridWrapperComponent, ReferralInputWithRevertComponent],
})
export class ReferralSiteDetailsComponent {
    constructor(
        private readonly _modalService: WeissmanModalService,
        private readonly _referralsService: ReferralsService,
        private readonly _busyService: BusyIndicatorService,
        private readonly _messageModalService: MessageModalService,
        private readonly _referralPageService: ReferralPageService,
        private readonly _toastr: ToastrService,
        private readonly _decimalPipe: DecimalPipe,
        private readonly _datePipe: WeissmanDateFormatPipe,
    ) {
        effect((onCleanup) => {
            const form = this.form();
            if (!form) return;
            const control = form.get('siteClassPrimary');
            if (!control) return;
            this._siteClassPrimary.set(control.value);
            const sub = control.valueChanges.subscribe((val) => {
                const previous = this._siteClassPrimary();
                this._siteClassPrimary.set(val);
                if (val !== previous) {
                    form.get('siteClassSecondary')?.setValue(null, { emitEvent: false });
                }
            });
            onCleanup(() => sub.unsubscribe());
        });

        effect(async () => {
            const isEditMode = this.editMode();

            if (!this._gridApi) return;

            this._gridApi.setColumnsVisible(['bigCheckbox'], isEditMode);
            this._gridApi.sizeColumnsToFit();
            this.isBulkDeleteVisible$.next(isEditMode && !!this.selectedAppeals.length);

            if (isEditMode && !this.siteClassPrimaryOptions) {
                const siteClasses = await this._referralsService.getLitigationReferralSiteClasses();
                this._siteClasses.set(siteClasses);
                this.siteClassPrimaryOptions = siteClasses.primarySiteClasses.map((x) => ({
                    name: x.name,
                    value: x.name,
                }));
            }
        });
    }

    referral = model<Core.LitigationReferralModel>();
    systemReferral: InputSignal<Core.LitigationReferralModel> = input<Core.LitigationReferralModel>();
    originalReferral: InputSignal<Core.LitigationReferralModel> = input<Core.LitigationReferralModel>();
    form: InputSignal<FormGroup> = input<FormGroup>();
    onSaveError = output<void>();
    onAppealAddRemove = output<void>();

    editMode = this._referralPageService.editMode;
    workflowStatus = this._referralPageService.workflowStatus;
    siteClassPrimaryOptions: Weissman.Model.NameValuePair[];

    gridId: Guid = '65b8ae86-d5e6-4279-aa62-d65fc92738ec';
    gridOptions: GridOptions = new AgGridOptionsBuilder()
        .withContext(this)
        .withLoadingOverlay()
        .withMultipleColumnSort()
        .withColumnResize()
        .withTextSelection()
        .withColumnPinning()
        .build();

    selectedAppeals: Core.LitigationReferralEligibleAppealModel[] = [];
    isBulkDeleteVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private _gridApi: GridApi;
    private _siteClasses = signal<Core.LitigationReferralSiteClassesModel>(null);
    private _siteClassPrimary = signal<string | null>(null);

    siteClassSecondaryOptions = computed(() => {
        const primarySiteClass = this._siteClassPrimary();
        if (!primarySiteClass) return [];
        const primarySiteClassObj = this._siteClasses()?.primarySiteClasses.find((x) => x.name === primarySiteClass);
        if (!primarySiteClassObj) return [];
        return primarySiteClassObj.secondarySiteClasses.map((s) => ({ name: s.name, value: s.name }));
    });

    onAgGridReady(grid: GridReadyEvent): void {
        this._gridApi = grid.api;

        const columns: ColDef[] = REFERRAL_APPEAL_COLUMNS(this._decimalPipe, this._datePipe);

        this._gridApi.setGridOption('columnDefs', columns);
        this._gridApi.setColumnsVisible(['bigCheckbox'], this.editMode());

        this.setGridData();
    }

    onFirstDataRendered(event: AgGridEvent): void {
        this._gridApi.setColumnsVisible(['bigCheckbox'], this.editMode());
    }

    setGridData() {
        if (this._gridApi) {
            this._gridApi.setGridOption('rowData', this.referral().referralAppeals);
        }
    }

    onSelectionChanged(event: any): void {
        this.selectedAppeals = this._gridApi.getSelectedRows();
        this.isBulkDeleteVisible$.next(this.editMode() && !!this.selectedAppeals.length);
    }

    async bulkDelete(): Promise<void> {
        if (this.referral().referralAppeals.length === 1) {
            this._toastr.error('Cannot remove the last remaining appeal.', 'Unable to Remove');
            return;
        }

        try {
            await this._messageModalService.confirm(
                'Are you sure you want to remove the selected Referral Appeals?',
                'Confirm Remove?',
            );
        } catch (e) {
            return;
        }

        this._saveBeforeAction(async () => {
            const busyRef = this._busyService.show({ message: 'Removing Referral Appeals' });
            try {
                await this._referralsService.removeAppealsFromLitigationReferral(
                    this.referral().litigationReferralId,
                    this.selectedAppeals.map((x) => x.appealId),
                );
                this.onAppealAddRemove.emit();
            } finally {
                busyRef.hide();
            }
        }, 'Referral must be saved before removing appeals.');
    }

    canSelectPrimary(): boolean {
        return (
            this.referral().referralAppeals.length > 1 &&
            this._gridApi.getSelectedRows().length === 1 &&
            !this._gridApi.getSelectedRows()[0].isPrimary
        );
    }

    async selectPrimary() {
        if (!this.canSelectPrimary()) {
            this._toastr.error('Please select a single Referral Appeal to set as primary.', 'Unable to Select');
            return;
        }
        try {
            await this._messageModalService.confirm(
                'Are you sure you want to select a new primary Appeal/Site for this Referral?',
                'Confirm Select Primary Appeal?',
            );
        } catch (e) {
            return;
        }

        this._saveBeforeAction(async () => {
            const busyRef = this._busyService.show({ message: 'Selecting Primary Appeal' });

            try {
                const appealId = this._gridApi.getSelectedRows()[0].appealId;
                await this._referralsService.setPrimaryAppeal(this.referral().litigationReferralId, appealId);
            } finally {
                busyRef.hide();
            }

            this.onAppealAddRemove.emit();
        }, 'Referral must be saved before selecting primary appeal.');
    }

    async syncAllAppeals() {
        try {
            await this._messageModalService.confirm(
                'Are you sure you want to sync all Referral Appeals from current data?',
                'Confirm Sync?',
            );
        } catch (e) {
            return;
        }

        this._saveBeforeAction(async () => {
            const busyRef = this._busyService.show({ message: 'Syncing Appeals' });

            try {
                await this._referralsService.syncAllAppeals(this.referral().litigationReferralId);
            } finally {
                busyRef.hide();
            }

            this.onAppealAddRemove.emit();
        }, 'Referral must be saved before syncing appeals.');
    }

    async launchAppealModal() {
        this._saveBeforeAction(async () => {
            const { didAdd } = await this._modalService.showAsync(
                AddAppealsModalComponent,
                { referralId: this.referral().litigationReferralId },
                'modal-xl',
            );

            if (didAdd) {
                this.onAppealAddRemove.emit();
            }
        }, 'Referral must be saved before adding appeals.');
    }

    private async _saveBeforeAction(action: () => Promise<void>, confirmMessage: string) {
        if (this.form().dirty) {
            try {
                await this._messageModalService.confirm(confirmMessage, 'Confirm Save');
            } catch (e) {
                return Promise.resolve(false);
            }

            const busyRef = this._busyService.show({ message: 'Saving' });

            try {
                this.referral.update((current) => ({ ...current, ...this.form().value }));
                await this._referralsService.updateLitigationReferral(this.referral());
                this._toastr.info('Referral changes autosaved');

                await action();
            } catch (e) {
                this.onSaveError.emit();

                throw e;
            } finally {
                busyRef.hide();
            }
        } else {
            await action();
        }
    }
}
